/**
 * Frido PDP — Thumbnail Links interactions.
 *
 *  - Opens the associated <dialog> on card click.
 *  - Closes on backdrop click, X button, or "Back to Shopping" button.
 *  - For the video variant: toggles muted state on the mute button and pauses
 *    the video when the dialog closes (so it doesn't keep playing in background).
 *  - ATC adds the currently selected variant directly. Listens to
 *    `variant:update` to keep the button label/disabled state in sync with the
 *    main PDP variant picker (SOLD OUT when the active variant is unavailable).
 *
 * Idempotent: safe to load on any page. Re-runs on every script load — the
 * `data-thumb-bound` attribute prevents double-binding.
 */

(function () {
  if (document.documentElement.dataset.thumbLinksBound === 'true') return;
  document.documentElement.dataset.thumbLinksBound = 'true';

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-thumb-open]');
    if (opener instanceof HTMLElement) {
      const id = opener.getAttribute('data-target');
      if (id) openDialog(id);
      return;
    }

    const closer = target.closest('[data-thumb-close]');
    if (closer instanceof HTMLElement) {
      const dialog = closer.closest('dialog.frido-thumb-dialog');
      if (dialog instanceof HTMLDialogElement) closeDialog(dialog);
      return;
    }

    const muteBtn = target.closest('[data-thumb-mute]');
    if (muteBtn instanceof HTMLElement) {
      const dialog = muteBtn.closest('dialog.frido-thumb-dialog');
      if (!(dialog instanceof HTMLDialogElement)) return;
      const video = dialog.querySelector('[data-thumb-video]');
      if (!(video instanceof HTMLVideoElement)) return;
      video.muted = !video.muted;
      muteBtn.setAttribute('aria-pressed', String(!video.muted));
      // Some browsers pause autoplay when unmuted; nudge it to keep playing.
      if (!video.muted) video.play().catch(() => {});
      return;
    }

    const shareBtn = target.closest('[data-thumb-share]');
    if (shareBtn instanceof HTMLElement) {
      const title = shareBtn.getAttribute('data-share-title') || document.title;
      const url = shareBtn.getAttribute('data-share-url') || window.location.href;
      handleShare({ title, url });
      return;
    }

    const atcBtn = target.closest('[data-thumb-atc]');
    if (atcBtn instanceof HTMLButtonElement) {
      if (atcBtn.disabled || atcBtn.getAttribute('aria-disabled') === 'true') return;
      const dialog = atcBtn.closest('dialog.frido-thumb-dialog');
      handleAddToCart(atcBtn, dialog instanceof HTMLDialogElement ? dialog : null);
      return;
    }

    // Backdrop click: native <dialog> fires click events with the dialog itself
    // as the target when the user clicks the backdrop area.
    if (target.matches('dialog.frido-thumb-dialog')) {
      closeDialog(target);
    }
  });

  // Keep popup ATC buttons in sync when the main PDP variant picker changes
  // the active variant.
  document.addEventListener('variant:update', (event) => {
    const variantId = event?.detail?.resource?.id;
    if (!variantId) return;
    document.querySelectorAll('[data-thumb-footer]').forEach((footer) => {
      const btn = footer.querySelector('[data-thumb-atc]');
      if (!(btn instanceof HTMLButtonElement)) return;
      btn.setAttribute('data-variant-id', String(variantId));
      applyAvailabilityState(btn, isVariantAvailable(footer, variantId));
    });
  });

  async function handleShare({ title, url }) {
    // Native Web Share API where available (mobile + most modern browsers).
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title, url });
        return;
      } catch (err) {
        // User cancelled (AbortError) — silently ignore. Anything else falls through.
        if (err && /** @type {any} */ (err).name === 'AbortError') return;
      }
    }
    // Fallback: copy URL to clipboard.
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // give up
      }
    }
  }

  function isVariantAvailable(footer, variantId) {
    const raw = footer.getAttribute('data-variant-avail');
    if (!raw) return true;
    try {
      const map = JSON.parse(raw);
      return map[String(variantId)] !== false;
    } catch {
      return true;
    }
  }

  function applyAvailabilityState(btn, available) {
    if (available) {
      btn.classList.remove('is-sold-out');
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.textContent = 'ADD TO CART';
    } else {
      btn.classList.add('is-sold-out');
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'SOLD OUT';
    }
  }

  async function handleAddToCart(button, dialog) {
    // Resolve variant ID: URL param → data attr fallback.
    const urlVariant = new URL(window.location.href).searchParams.get('variant');
    const variantId = urlVariant || button.getAttribute('data-variant-id');
    if (!variantId) return;

    button.setAttribute('data-loading', 'true');
    button.disabled = true;
    button.textContent = 'ADDING…';

    try {
      // Match the theme's own ATC request shape exactly (see product-form.js).
      // Raw JSON or FormData without these specific headers trips Shopify's
      // bot challenge ("Your connection needs to be verified before you can
      // proceed").
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      // Pass cart-section IDs so the response includes section-rerender HTML —
      // not required for the add, but matches the theme's well-known shape.
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((el) => el.getAttribute('data-section-id'))
        .filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));
      const addUrl = (window.Theme?.routes?.cart_add_url) || '/cart/add.js';
      const res = await fetch(addUrl, {
        method: 'POST',
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      // Dispatch the theme's cart:update event so the cart drawer can auto-open
      // and bubbles/UI update. Matches the convention in CLAUDE.md.
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: null,
            sourceId: 'frido-thumb-dialog',
            data: {
              source: 'frido-thumb-dialog',
              productId: button.closest('[data-product-id]')?.getAttribute('data-product-id') || null,
              variantId,
              itemCount: 1,
            },
          },
        })
      );

      button.textContent = 'ADDED';
      // Close the dialog after a short beat so user sees the confirmation.
      setTimeout(() => {
        if (dialog) closeDialog(dialog);
        button.removeAttribute('data-loading');
        const footer = button.closest('[data-thumb-footer]');
        const available = footer instanceof HTMLElement ? isVariantAvailable(footer, variantId) : true;
        applyAvailabilityState(button, available);
      }, 800);
    } catch (err) {
      console.error('[frido-thumb-dialog] ATC failed:', err);
      button.textContent = 'TRY AGAIN';
      setTimeout(() => {
        button.removeAttribute('data-loading');
        const footer = button.closest('[data-thumb-footer]');
        const available = footer instanceof HTMLElement ? isVariantAvailable(footer, variantId) : true;
        applyAvailabilityState(button, available);
      }, 1500);
    }
  }

  function openDialog(id) {
    const dialog = document.getElementById(id);
    if (!(dialog instanceof HTMLDialogElement)) return;
    try {
      dialog.showModal();
    } catch {
      dialog.setAttribute('open', '');
    }
    // Lock the page behind the dialog — showModal() alone doesn't stop the body
    // from scrolling. Uses the theme's own lock (base.css: html[scroll-lock]{overflow:hidden}).
    document.documentElement.setAttribute('scroll-lock', '');
    const video = dialog.querySelector('[data-thumb-video]');
    if (video instanceof HTMLVideoElement) {
      const src = video.dataset.thumbSrc;
      if (src && video.src !== src) video.src = src;
      video.currentTime = 0;
      video.play().catch(() => {});
    }
  }

  function closeDialog(dialog) {
    if (dialog.__thumbClosing) return;
    // Pause playback immediately so audio stops the moment close is requested,
    // but keep the last frame visible through the exit animation.
    const video = dialog.querySelector('[data-thumb-video]');
    if (video instanceof HTMLVideoElement) video.pause();

    const cleanup = () => {
      if (video instanceof HTMLVideoElement) {
        // Release the connection so a closed dialog doesn't keep network state
        // alive. Restored from data-thumb-src the next time the dialog opens.
        video.removeAttribute('src');
        video.load();
        // Reset mute state so reopening starts muted again (matches autoplay policy).
        video.muted = true;
        const muteBtn = dialog.querySelector('[data-thumb-mute]');
        if (muteBtn instanceof HTMLElement) muteBtn.setAttribute('aria-pressed', 'false');
      }
      try {
        dialog.close();
      } catch {
        dialog.removeAttribute('open');
      }
      document.documentElement.removeAttribute('scroll-lock');
    };

    // Play the exit animation (--closing) before actually closing. Falls back to
    // an immediate close under reduced-motion / if animationend doesn't fire.
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !dialog.open) {
      cleanup();
      return;
    }
    dialog.__thumbClosing = true;
    dialog.classList.add('frido-thumb-dialog--closing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      dialog.removeEventListener('animationend', onEnd);
      dialog.classList.remove('frido-thumb-dialog--closing');
      dialog.__thumbClosing = false;
      cleanup();
    };
    const onEnd = (e) => {
      if (e.target === dialog && /frido-thumb-out/.test(e.animationName)) finish();
    };
    dialog.addEventListener('animationend', onEnd);
    setTimeout(finish, 460); // fallback > the longest exit (0.3s)
  }
})();
